import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { observer } from 'mobx-react-lite'
import {
  Box, Button, Chip, CircularProgress, Divider, IconButton, Paper, Stack,
  Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tabs, Tooltip, Typography,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Domain as OrgIcon,
  AccountTree as LobIcon,
  Business as BusinessIcon,
} from '@mui/icons-material'
import { toast } from 'react-toastify'
import { PageHeader } from '../../components/shared/PageHeader'
import { DeleteDialog } from '../../components/shared/DeleteDialog'
import { ConfirmDialog } from '../../components/shared/ConfirmDialog'
import { StatCard } from '../../components/shared/StatCard'
import { FormInput, FormSelect, FormDialog } from '../../components/ui'
import { themeColors } from '../../app/layout/theme'
import { orgsApi, lobsApi, Org, Lob } from '../../lib/api/tenantsApi'

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}


// ══════════════════════════════════════════════════════════════════════════════
// ORGANISATIONS TAB
// ══════════════════════════════════════════════════════════════════════════════

function OrgFormDialog({ open, org, onClose }: { open: boolean; org?: Org | null; onClose: () => void }) {
  const qc = useQueryClient()
  const isEdit = !!org
  const [name, setName] = useState(org?.name ?? '')
  const [error, setError] = useState('')
  const [isSaveConfirmOpen, setIsSaveConfirmOpen] = useState(false)

  useMemo(() => {
    if (open) {
      setName(org?.name ?? '')
      setError('')
      setIsSaveConfirmOpen(false)
    }
  }, [open, org])

  const mutation = useMutation({
    mutationFn: () => isEdit ? orgsApi.update(org!.id, name.trim()) : orgsApi.create(name.trim()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-orgs'] })
      toast.success(isEdit ? 'Organisation updated' : 'Organisation created')
      onClose()
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to save'),
  })

  const handleSave = () => {
    if (!name.trim()) { setError('Name is required'); return }
    if (isEdit) {
      setIsSaveConfirmOpen(true)
    } else {
      mutation.mutate()
    }
  }

  const handleConfirmSave = () => {
    setIsSaveConfirmOpen(false)
    mutation.mutate()
  }

  return (
    <>
      <FormDialog
        open={open}
        onClose={onClose}
        onSave={handleSave}
        title={isEdit ? 'Edit Organisation' : 'New Organisation'}
        saveLabel={isEdit ? 'Save Changes' : 'Create'}
        saving={mutation.isPending}
        maxWidth="xs"
      >
        <FormInput
          label="Organisation Name"
          required
          value={name}
          onChange={e => { setName(e.target.value); setError('') }}
          error={!!error}
          helperText={error}
          fullWidth
          autoFocus
          placeholder="e.g. Acme Corp"
        />
      </FormDialog>

      <ConfirmDialog
        open={isSaveConfirmOpen}
        title="Save Changes"
        description="Are you sure you want to save the changes to this organisation?"
        confirmLabel="Save"
        confirmColor="primary"
        onClose={() => setIsSaveConfirmOpen(false)}
        onConfirm={handleConfirmSave}
      />
    </>
  )
}

function OrgsTab() {
  const qc = useQueryClient()
  const [formOpen, setFormOpen] = useState(false)
  const [editOrg, setEditOrg] = useState<Org | null>(null)
  const [deleteOrg, setDeleteOrg] = useState<Org | null>(null)

  const { data: orgs = [], isLoading } = useQuery({
    queryKey: ['admin-orgs'],
    queryFn: orgsApi.getAll,
  })

  const deleteMutation = useMutation({
    mutationFn: () => orgsApi.delete(deleteOrg!.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-orgs'] })
      qc.invalidateQueries({ queryKey: ['admin-lobs'] })
      toast.success('Organisation deleted')
      setDeleteOrg(null)
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to delete'),
  })

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained" size="small" startIcon={<AddIcon />}
          onClick={() => { setEditOrg(null); setFormOpen(true) }}
          sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, background: themeColors.primary, '&:hover': { background: themeColors.primaryDark } }}>
          New Organisation
        </Button>
      </Box>

      <TableContainer component={Paper} elevation={0}
        sx={{ border: `1px solid ${themeColors.border}`, borderRadius: '12px', overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f7fa' }}>
              <TableCell sx={{ fontWeight: 700, fontSize: '12px', color: themeColors.text.secondary, letterSpacing: '0.5px' }}>NAME</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '12px', color: themeColors.text.secondary, letterSpacing: '0.5px' }}>CREATED</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '12px', color: themeColors.text.secondary }} align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={3} sx={{ textAlign: 'center', py: 5 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : orgs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} sx={{ textAlign: 'center', py: 6 }}>
                  <OrgIcon sx={{ fontSize: 40, color: 'rgba(0,0,0,0.12)', mb: 1, display: 'block', mx: 'auto' }} />
                  <Typography fontWeight={600} color="text.secondary">No organisations yet</Typography>
                  <Typography variant="caption" color="text.secondary">Create your first organisation to get started</Typography>
                </TableCell>
              </TableRow>
            ) : orgs.map((org, idx) => (
              <TableRow key={org.id}
                sx={{ bgcolor: idx % 2 === 0 ? '#fff' : '#fafafa', '&:hover': { bgcolor: '#f2f4f7' } }}>
                <TableCell sx={{ py: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                      width: 32, height: 32, borderRadius: '8px', bgcolor: themeColors.primaryLight,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <BusinessIcon sx={{ fontSize: 16, color: '#ffffff' }} />
                    </Box>
                    <Typography fontWeight={600} fontSize="13.5px">{org.name}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography fontSize="12.5px" color="text.secondary">{formatDate(org.createdAt)}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => { setEditOrg(org); setFormOpen(true) }}
                        sx={{ color: '#9ca3af', '&:hover': { color: themeColors.primary, bgcolor: '#F2EEF9' } }}>
                        <EditIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => setDeleteOrg(org)}
                        sx={{ color: themeColors.text.muted, '&:hover': { color: themeColors.error, bgcolor: themeColors.errorLight } }}>
                        <DeleteIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <OrgFormDialog open={formOpen} org={editOrg} onClose={() => setFormOpen(false)} />
      <DeleteDialog
        open={!!deleteOrg}
        title="Delete Organisation"
        name={deleteOrg?.name}
        description={deleteOrg ? `Are you sure you want to delete ${deleteOrg.name}? This action cannot be undone and will cascade-delete all linked data.` : undefined}
        onClose={() => setDeleteOrg(null)}
        onConfirm={() => deleteMutation.mutate()}
        isDeleting={deleteMutation.isPending}
      />
    </Box>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// LINES OF BUSINESS TAB
// ══════════════════════════════════════════════════════════════════════════════

function LobFormDialog({ open, lob, orgs, onClose, defaultOrgId = '' }: { open: boolean; lob?: Lob | null; orgs: Org[]; onClose: () => void; defaultOrgId?: string }) {
  const qc = useQueryClient()
  const isEdit = !!lob
  const [name, setName] = useState(lob?.name ?? '')
  const [orgId, setOrgId] = useState(lob?.orgId ?? defaultOrgId)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSaveConfirmOpen, setIsSaveConfirmOpen] = useState(false)

  useMemo(() => {
    if (open) {
      setName(lob?.name ?? '')
      setOrgId(lob?.orgId ?? defaultOrgId)
      setErrors({})
      setIsSaveConfirmOpen(false)
    }
  }, [open, lob, defaultOrgId])

  const mutation = useMutation({
    mutationFn: () => isEdit ? lobsApi.update(lob!.id, name.trim()) : lobsApi.create(orgId, name.trim()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-lobs'] })
      toast.success(isEdit ? 'LOB updated' : 'Line of Business created')
      onClose()
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to save'),
  })

  const handleSave = () => {
    const e: Record<string, string> = {}
    if (!name.trim()) e.name = 'Name is required'
    if (!isEdit && !orgId) e.orgId = 'Organisation is required'
    if (Object.keys(e).length) { setErrors(e); return }
    if (isEdit) {
      setIsSaveConfirmOpen(true)
    } else {
      mutation.mutate()
    }
  }

  const handleConfirmSave = () => {
    setIsSaveConfirmOpen(false)
    mutation.mutate()
  }

  return (
    <>
      <FormDialog
        open={open}
        onClose={onClose}
        onSave={handleSave}
        title={isEdit ? 'Edit Line of Business' : 'New Line of Business'}
        saveLabel={isEdit ? 'Save Changes' : 'Create'}
        saving={mutation.isPending}
        maxWidth="xs"
      >
        <Stack spacing={2.5}>
          {!isEdit && (
            <FormSelect
              label="Organisation"
              required
              value={orgId}
              onChange={v => { setOrgId((v as any)?.value ?? ''); setErrors(p => ({ ...p, orgId: '' })) }}
              options={orgs.map(o => ({ label: o.name, value: o.id }))}
              error={!!errors.orgId}
              helperText={errors.orgId}
              fullWidth
            />
          )}
          <FormInput
            label="LOB Name"
            required
            value={name}
            onChange={e => { setName(e.target.value); setErrors(p => ({ ...p, name: '' })) }}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
            autoFocus={isEdit}
            placeholder="e.g. Retail Sales"
          />
        </Stack>
      </FormDialog>

      <ConfirmDialog
        open={isSaveConfirmOpen}
        title="Save Changes"
        description="Are you sure you want to save the changes to this line of business?"
        confirmLabel="Save"
        confirmColor="primary"
        onClose={() => setIsSaveConfirmOpen(false)}
        onConfirm={handleConfirmSave}
      />
    </>
  )
}

function LobsTab({ orgs }: { orgs: Org[] }) {
  const qc = useQueryClient()
  const [formOpen, setFormOpen] = useState(false)
  const [editLob, setEditLob] = useState<Lob | null>(null)
  const [deleteLob, setDeleteLob] = useState<Lob | null>(null)
  const [filterOrg, setFilterOrg] = useState('')

  const { data: lobs = [], isLoading } = useQuery({
    queryKey: ['admin-lobs', filterOrg],
    queryFn: () => lobsApi.getAll(filterOrg || undefined),
  })

  const deleteMutation = useMutation({
    mutationFn: () => lobsApi.delete(deleteLob!.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-lobs'] })
      toast.success('Line of Business deleted')
      setDeleteLob(null)
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to delete'),
  })

  const orgName = (id: string) => orgs.find(o => o.id === id)?.name ?? id.slice(-8)

  const orgFilterOptions = [
    { label: 'All Organisations', value: '' },
    ...orgs.map(o => ({ label: o.name, value: o.id }))
  ]

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'nowrap' }}>
        <FormSelect
          value={orgFilterOptions.find(o => o.value === filterOrg) ?? null}
          onChange={v => setFilterOrg((v as any)?.value ?? '')}
          options={orgFilterOptions}
          sx={{ width: 220, flexShrink: 0 }}
        />
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained" size="small" startIcon={<AddIcon />}
          onClick={() => { setEditLob(null); setFormOpen(true) }}
          sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, background: themeColors.primary, '&:hover': { background: themeColors.primaryDark }, flexShrink: 0 }}>
          New LOB
        </Button>
      </Box>

      <TableContainer component={Paper} elevation={0}
        sx={{ border: `1px solid ${themeColors.border}`, borderRadius: '12px', overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f7fa' }}>
              <TableCell sx={{ fontWeight: 700, fontSize: '12px', color: themeColors.text.secondary, letterSpacing: '0.5px' }}>NAME</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '12px', color: themeColors.text.secondary, letterSpacing: '0.5px' }}>ORGANISATION</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '12px', color: themeColors.text.secondary, letterSpacing: '0.5px' }}>CREATED</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={4} sx={{ textAlign: 'center', py: 5 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : lobs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} sx={{ textAlign: 'center', py: 6 }}>
                  <LobIcon sx={{ fontSize: 40, color: 'rgba(0,0,0,0.12)', mb: 1, display: 'block', mx: 'auto' }} />
                  <Typography fontWeight={600} color="text.secondary">No lines of business yet</Typography>
                </TableCell>
              </TableRow>
            ) : lobs.map((lob, idx) => (
              <TableRow key={lob.id}
                sx={{ bgcolor: idx % 2 === 0 ? '#fff' : '#fafafa', '&:hover': { bgcolor: '#f2f4f7' } }}>
                <TableCell sx={{ py: 1.5 }}>
                  <Typography fontWeight={600} fontSize="13.5px">{lob.name}</Typography>
                </TableCell>
                <TableCell>
                  <Chip label={orgName(lob.orgId)} size="small"
                    sx={{ height: 20, fontSize: '10.5px', fontWeight: 600, bgcolor: themeColors.primaryLight, color: '#ffffff' }} />
                </TableCell>
                <TableCell>
                  <Typography fontSize="12.5px" color="text.secondary">{formatDate(lob.createdAt)}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => { setEditLob(lob); setFormOpen(true) }}
                        sx={{ color: '#9ca3af', '&:hover': { color: themeColors.primary, bgcolor: '#F2EEF9' } }}>
                        <EditIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => setDeleteLob(lob)}
                        sx={{ color: themeColors.text.muted, '&:hover': { color: themeColors.error, bgcolor: themeColors.errorLight } }}>
                        <DeleteIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <LobFormDialog open={formOpen} lob={editLob} orgs={orgs} defaultOrgId={filterOrg} onClose={() => setFormOpen(false)} />
      <DeleteDialog
        open={!!deleteLob}
        title="Delete Line of Business"
        name={deleteLob?.name}
        description={deleteLob ? `Are you sure you want to delete ${deleteLob.name}? This action cannot be undone and will cascade-delete all linked data.` : undefined}
        onClose={() => setDeleteLob(null)}
        onConfirm={() => deleteMutation.mutate()}
        isDeleting={deleteMutation.isPending}
      />
    </Box>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════════════════════════

const TenantAdminPage = observer(function TenantAdminPage() {
  const [tab, setTab] = useState(0)

  const { data: orgs = [] } = useQuery({
    queryKey: ['admin-orgs'],
    queryFn: orgsApi.getAll,
    staleTime: 60_000,
  })

  const tabSx = {
    '& .MuiTab-root': { fontSize: '13px', fontWeight: 600, textTransform: 'none', minHeight: 44 },
    '& .Mui-selected': { color: themeColors.primary },
    '& .MuiTabs-indicator': { backgroundColor: themeColors.primary, height: 3, borderRadius: '3px 3px 0 0' },
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        title="Tenant Settings"
        breadcrumb={[{ label: 'Settings', to: '/settings' }]}
        showBreadcrumb
      />
      <Box sx={{ flex: 1, p: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 2, mb: 3 }}>
          <StatCard
            title="Organisations"
            value={orgs.length}
            icon={<BusinessIcon />}
            color={themeColors.primary}
            gradient={themeColors.gradient.blue}
          />
        </Box>

        <Box sx={{ borderBottom: `1px solid ${themeColors.border}`, mb: 3 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={tabSx}>
            <Tab icon={<BusinessIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Organisations" />
            <Tab icon={<LobIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Lines of Business" />
          </Tabs>
        </Box>

        {tab === 0 && <OrgsTab />}
        {tab === 1 && <LobsTab orgs={orgs} />}
      </Box>
    </Box>
  )
})

export default TenantAdminPage
